import { Component } from 'preact';
import { useLayoutEffect, useRef, useState } from 'preact/hooks';
import { motion, AnimatePresence } from 'framer-motion';
import {
  GOLD, ACCENT_GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, BG_ATMOSPHERE, VIGNETTE,
  KnotOrnament,
} from '../lib/medievalTheme';
import { CloseButton } from './ui/navButtons';
import {
  IconRuneFehu, IconRuneKenaz, IconRuneAnsuz, IconRuneAlgiz, IconRuneDagaz,
  CoinIcon, COIN_ICON_COLOR,
} from './ui/icons';
import { toast } from 'sonner';
import { playUI, UI } from '../utils/arena/uiSounds';
import {
  SKILL_PATHS,
  HUNTER_ELEMENTS,
  TOTAL_SKILL_POINTS,
  getSkillState,
  getAvailableSkillPoints,
  getNodeStatus,
  getInvestmentBlocker,
  countSpent,
  spendPoint,
  setHunterElement,
  purchaseSkillReset,
  SKILL_RESET_COST,
  getNodeMinLevel,
} from '../utils/arena/skills';
import { isDevBuild } from '../utils/devMode';
import { askConfirm } from '../utils/confirmBus';
import { levelFromXp } from '../utils/arena/profileDefaults';
import TutorialOverlay from './TutorialOverlay';
import { shouldAutoPlay, markTutorialSeen, hydrateTutorialState } from '../utils/arena/tutorialState';
import { t } from '../localization/t';

const SKILL_TREE_TUTORIAL_KEY = 'skill-tree';
// Step 1 has no selector. It's a centered intro card. The rest
// spotlight DOM elements via `data-tutorial="…"` so the overlay's
// arrow + cutout point at exactly the bit of UI the copy is talking
// about. data-tutorial attributes live in this file's render method.
const skillTreeTutorialSteps = () => [
  {
    key: 'welcome',
    title: t('Welcome to Mastery'),
    body: t('Five paths of meta-progression. Every two player levels you earn one Skill Point. Spend them down any path to unlock permanent perks. With 15 points total at the level cap, you can fully invest in two paths and half of a third, so commit to the playstyle you actually want.'),
  },
  {
    key: 'paths',
    title: t('The Five Paths'),
    body: t('Reliquarian (Auction House mastery), Inscription (crafting + disenchanting), Battle Mage (Season Pass + Trials rewards), Augmentation (foil affinity for one element), Transmutation (booster pack rerolls). Each path has 6 nodes ending in a capstone with a unique signature effect.'),
    selector: '[data-tutorial="skill-paths-grid"]',
  },
  {
    key: 'budget',
    title: t('Skill Point Budget'),
    body: t('The HUD in the corner tracks your unspent points and the lit pips show what you have available vs. earned. Players cap out at 15 points lifetime, so choose carefully. If you want to re-spec later, Reset Skill Tree refunds every spent point for 500 gold.'),
    selector: '[data-tutorial="skill-points-hud"]',
  },
  {
    key: 'gates',
    title: t('Prereqs and Level Gates'),
    body: t('Each path is sequential: the third skill unlocks at level 10, the capstone at level 20. Click a node to commit a point; the tooltip will tell you exactly what the perk does and what level you need.'),
  },
  {
    key: 'augmentation',
    title: t('A Note on Augmentation'),
    body: t('The Augmentation path is special: clicking its first skill prompts you to pick one of four elements (Air, Earth, Fire, Water). That choice is locked in (only a skill tree reset clears it); every Foil Affinity perk in the path will then apply to that element only.'),
  },
];

// Each path is keyed by an Elder Futhark rune. Drawn as
// stroke-based SVGs so they accept the path's accent color via
// `style.color` and look the same in the path header (36px) as
// they do in the capstone medallion (28px) and the tooltip
// ornament (the .ai-derived background medallion).
const PATH_ICONS = {
  merchant: IconRuneFehu,    // ᚠ. Wealth
  spellcraft: IconRuneKenaz, // ᚲ. Creation / torch
  scholar: IconRuneAnsuz,    // ᚨ. Wisdom
  hunter: IconRuneAlgiz,     // ᛉ. Elk / protection
  transmuter: IconRuneDagaz, // ᛞ. Dawn / transformation
};

// Tooltip sizing in DESIGN pixels (multiplied by canvasScale at use-site).
// CSS sets the tooltip width at 20cqw which equals 320 design-px at the
// 1600px design canvas. Keep this constant in sync with that rule.
const TOOLTIP_W = 320;
const TOOLTIP_H_EST = 140;
const TOOLTIP_MARGIN = 18;
const VIEWPORT_PAD = 16;

// Compute a clamped position for the tooltip given a node's bounding rect
// and the tooltip's actual measured height. Both inputs are in viewport
// coordinates (getBoundingClientRect returns viewport pixels). The tooltip
// is rendered with `position: fixed` inside `.app-canvas`, which is a
// containing block (transform: translateZ(0)), so the returned left/top
// are in canvas-local coordinates. We subtract the canvas's offset to
// convert. This way the tooltip stays correctly placed regardless of any
// letterbox bars between the canvas and the viewport.
//
// `height` is the *rendered* tooltip height in viewport pixels. Pass
// getBoundingClientRect().height after first paint, or scale TOOLTIP_H_EST
// by canvasScale for the initial estimate.
//
// All sizing math (TOOLTIP_W, TOOLTIP_MARGIN, VIEWPORT_PAD) is multiplied
// by canvasScale so the JS-side clamp matches the CSS-side cqw-driven
// rendered dimensions. Without this, on a 4K canvas the JS thinks the
// tooltip is still 320 design-px while the CSS actually paints it at 768
// real pixels, and the right-edge / vertical clamp logic underestimates
// how much room the tooltip needs.
function positionTooltip(rect, height = TOOLTIP_H_EST) {
  if (!rect) return null;
  const canvasEl = typeof document !== 'undefined' ? document.querySelector('.app-canvas') : null;
  const canvasRect = canvasEl ? canvasEl.getBoundingClientRect() : { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight };
  const canvasW = canvasRect.width;
  const canvasH = canvasRect.height;
  const canvasScale = canvasW / 1600;

  // Convert node rect from viewport coords to canvas-local coords.
  const localLeft = rect.left - canvasRect.left;
  const localTop = rect.top - canvasRect.top;
  const localRight = rect.right - canvasRect.left;
  const localBottom = rect.bottom - canvasRect.top;
  const cx = localLeft + rect.width / 2;
  const cy = localTop + rect.height / 2;

  // Tooltip dimensions and margins, scaled to match the cqw-rendered size.
  const tooltipW = TOOLTIP_W * canvasScale;
  const margin = TOOLTIP_MARGIN * canvasScale;
  const pad = VIEWPORT_PAD * canvasScale;

  // Default. Right of node, vertically centered.
  let left = localRight + margin;
  let top = cy - height / 2;

  if (left + tooltipW > canvasW - pad) {
    // Flip to the left of the node.
    left = localLeft - tooltipW - margin;
  }
  if (left < pad) {
    // No horizontal room either side. Center horizontally above or below.
    left = cx - tooltipW / 2;
    if (localTop - height - margin > pad) {
      top = localTop - height - margin;
    } else {
      top = localBottom + margin;
    }
  }

  // Final clamp keeps the entire tooltip inside the canvas.
  left = Math.max(pad, Math.min(left, canvasW - tooltipW - pad));
  top = Math.max(pad, Math.min(top, canvasH - height - pad));
  return { left, top };
}

function SkillTooltip({ path, node, anchorRect, pinned, confirming, onMeasure }) {
  const tooltipRef = useRef(null);
  // First render uses a canvasScale-aware height estimate so the initial
  // placement is roughly right. useLayoutEffect below re-measures the
  // actual rendered height and re-positions before paint so tall
  // tooltips near the canvas edge get pulled inside cleanly.
  const initialCanvasScale = typeof document !== 'undefined'
    ? (document.querySelector('.app-canvas')?.getBoundingClientRect().width || 1600) / 1600
    : 1;
  const [pos, setPos] = useState(() => positionTooltip(anchorRect, TOOLTIP_H_EST * initialCanvasScale));

  useLayoutEffect(() => {
    if (!anchorRect || !tooltipRef.current) return;
    const measuredH = tooltipRef.current.getBoundingClientRect().height;
    if (measuredH <= 0) return;
    const next = positionTooltip(anchorRect, measuredH);
    setPos((prev) =>
      prev && next && prev.left === next.left && prev.top === next.top
        ? prev
        : next,
    );
    // Lift the measured height to whoever owns the tooltip so the
    // confirm bar (a sibling component) can call positionTooltip
    // with the same height we did and end up anchored to the
    // tooltip's *actual* top edge. Not the static-estimate one.
    // Without this, the bar floats at the cy-70 default while the
    // tooltip drifts upward by (measuredH-140)/2 once measured,
    // and the gap between them collapses into an overlap.
    if (onMeasure) onMeasure(measuredH);
  }, [anchorRect, node.key, confirming, onMeasure]);

  if (!pos) return null;
  // Strip the leading "The " from path titles so the small label
  // reads cleaner ("MERCHANT" not "THE MERCHANT").
  const pathLabel = (path.title || '').replace(/^The\s+/i, '');
  const cls = `vh-skill-tooltip${pinned ? ' vh-skill-tooltip-pinned' : ''}${confirming ? ' vh-skill-tooltip-confirming' : ''}`;
  // Wrapper carries the screen-space position; inner motion.div
  // carries the framer animation (which uses transform).
  return (
    <div
      key={node.key}
      style={{ position: 'fixed', left: pos.left, top: pos.top, pointerEvents: pinned ? 'auto' : 'none', zIndex: 200 }}
    >
      <motion.div
        ref={tooltipRef}
        initial={{ opacity: 0, scale: 0.96, y: 4 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 4 }}
        transition={{ duration: 0.16, ease: [0.22, 0.8, 0.36, 1] }}
        className={cls}
        style={{
          // Relative (not static) so the absolutely-positioned
          // .vh-skill-tooltip-medallion inside still uses this
          // element as its containing block and gets clipped by
          // the tooltip's `overflow: hidden`. Static would let the
          // medallion escape outward and use the outer wrapper as
          // its containing block, which has no clip.
          position: 'relative',
                    '--vh-tooltip-accent': path.accent,
          '--vh-tooltip-accent-glow': `${path.accent}b3`, // ~70% alpha for the glow halo
          '--vh-tooltip-glow': `${path.accent}33`,
          // Path-specific Viking ornament SVG. Bleeds out of the
          // tooltip's left edge as a faded medallion. URL is set via
          // inline style because Bun's CSS minifier strips url() from
          // .css files.
          '--vh-tooltip-medallion-svg': `url("/ornaments/path-${path.key}.svg")`,
        }}
      >
      {/* Background ornament. Sits at z-index -1 behind the
          content. Fades from full opacity at the tooltip's left
          edge to transparent toward the right via a composited
          gradient mask. */}
      <div className="vh-skill-tooltip-medallion" aria-hidden="true" />

      {/* Confirming sweep. A bright accent gradient that travels
          across the tooltip when the player commits, reading as a
          rune of light burning into the parchment. Only rendered
          while confirming so it doesn't cost layout otherwise. */}
      {confirming ? (
        <div className="vh-skill-tooltip-sweep" aria-hidden="true" />
      ) : null}

      <div className="vh-skill-tooltip-accent" />
      {/* Capstone badge. Absolutely-pinned to the tooltip's top-
          right corner so it never crowds the title regardless of
          name length. The path label / title row reserves matching
          padding-right so long titles don't slide under it. */}
      {node.capstone ? (
        <span className="vh-skill-tooltip-capstone-badge">{t('Capstone')}</span>
      ) : null}
      <div
        className={`vh-skill-tooltip-path${node.capstone ? ' vh-skill-tooltip-path-with-capstone' : ''}`}
        style={{ color: path.accent }}
      >
        {pathLabel}
      </div>
      <div className={`vh-skill-tooltip-title-row${node.capstone ? ' vh-skill-tooltip-title-row-with-capstone' : ''}`}>
        <h3 className="vh-skill-tooltip-title arena-heading">{node.title}</h3>
      </div>
      <div className="vh-skill-tooltip-divider" />
      <p className="vh-skill-tooltip-body">{node.desc}</p>
      {node.levelLockNeeded ? (
        <p className="vh-skill-tooltip-level-lock">
          {t('Requires Level {n}', { n: node.levelLockNeeded })}
        </p>
      ) : null}
      </motion.div>
    </div>
  );
}

/**
 * Confirm/cancel bar. Floats above the pinned tooltip, sized to
 * match its width and pinned to the same `pos.left`. Sibling of the
 * tooltip rather than a child so the tooltip's own dimensions stay
 * identical to the hover state (no layout shift on click).
 */
// Vertical clearance between the bar's bottom edge and the tooltip's
// top edge, in design-pixels.
const CONFIRM_BAR_GAP = 10;
// Initial bar-height guess used on the first render before the post-
// layout measurement runs.
const CONFIRM_BAR_FALLBACK_H = 38;

function SkillTooltipConfirmBar({ path, anchorRect, confirming, onConfirm, onCancel, tooltipHeight }) {
  const barRef = useRef(null);
  const initialCanvasScale = typeof document !== 'undefined'
    ? (document.querySelector('.app-canvas')?.getBoundingClientRect().width || 1600) / 1600
    : 1;
  // Use the tooltip's actually-measured height (lifted up via
  // SkillTooltip's onMeasure callback) so positionTooltip returns
  // the same `top` the tooltip is rendered at. Falling back to a
  // canvasScale-adjusted estimate for the first frame.
  const pos = positionTooltip(anchorRect, tooltipHeight ?? (TOOLTIP_H_EST * initialCanvasScale));
  const [barHeight, setBarHeight] = useState(CONFIRM_BAR_FALLBACK_H * initialCanvasScale);

  useLayoutEffect(() => {
    if (!barRef.current) return;
    const h = barRef.current.getBoundingClientRect().height;
    if (h > 0 && Math.abs(h - barHeight) > 0.5) setBarHeight(h);
  }, [anchorRect, confirming, barHeight]);

  if (!pos) return null;

  return (
    <div
      key="confirm-bar"
      style={{ position: 'fixed', left: pos.left, top: pos.top - barHeight - CONFIRM_BAR_GAP, zIndex: 201 }}
    >
      <motion.div
        ref={barRef}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 6 }}
        transition={{ duration: 0.14, ease: [0.22, 0.8, 0.36, 1] }}
        className="vh-skill-tooltip-confirm-bar"
        style={{
          position: 'relative',
                    '--vh-tooltip-accent': path.accent,
          '--vh-tooltip-accent-glow': `${path.accent}b3`,
        }}
      >
        <button
          type="button"
          className="vh-skill-tooltip-btn vh-skill-tooltip-btn-cancel"
          onClick={onCancel}
          disabled={confirming}
        >
          {t('Cancel')}
        </button>
        <button
          type="button"
          className="vh-skill-tooltip-btn vh-skill-tooltip-btn-confirm"
          onClick={onConfirm}
          disabled={confirming}
        >
          ◆ {t('Confirm')}
        </button>
      </motion.div>
    </div>
  );
}

/**
 * Skill tree screen. Fullscreen meta-progression UI. Five paths
 * (Merchant, Artisan, Scholar, Hunter, Curator) of six nodes each,
 * 30 nodes / 30 points / level 30 cap. Two paths require a player
 * choice before any point can be invested: Hunter picks a card
 * subtype to focus foil-affinity on, Curator picks a set.
 */
export default class SkillTreeScreen extends Component {
  constructor(props) {
    super(props);
    // Read-only viewing of another player's tree. When `viewProfile`
    // is set, the screen seeds its skill state from that profile's
    // `skills` blob (the public profile endpoint already returns it),
    // and every spend / reset / element-pick handler early-returns so
    // the visitor can't mutate someone else's tree.
    const readOnly = !!props.viewProfile;
    const sourceSkills = readOnly
      ? (props.viewProfile?.skills || { pointsSpent: [], hunterElement: null })
      : getSkillState();
    this.state = {
      skillState: sourceSkills,
      hoveredNode: null,
      pickerPath: null, // path key whose element picker is open
      // When the picker is opened from a node-click flow (rather than
      // a standalone "Choose Element" button), we hold the node here
      // so we can chain straight into the purchase confirmation as
      // soon as the element is picked.
      pickerPendingNode: null,
      // Active purchase confirmation. Skill spends are permanent so
      // every click goes through this gate before spendPoint() runs.
      pendingPurchase: null, // { path, node } | null
      // Most-recent measured height of the pinned tooltip. Lifted
      // up here so the SkillTooltipConfirmBar (a sibling of the
      // tooltip) can call positionTooltip with the same height the
      // tooltip ends up using post-layout. Without it the bar
      // floats relative to the 140px estimate while the tooltip
      // drifts to its measured top, which collapses the gap into a
      // visible overlap.
      pinnedTooltipHeight: null,
      showSkillTreeTutorial: false,
      // In-flight paid respec. Latches the button so a double click
      // can't fire two charges before the first response lands.
      resetting: false,
    };
  }

  // Stable identity (class arrow) so SkillTooltip's useLayoutEffect
  // doesn't see a fresh prop reference each render and re-fire.
  handlePinnedTooltipMeasure = (h) => {
    if (!h || h <= 0) return;
    const prev = this.state.pinnedTooltipHeight;
    if (prev != null && Math.abs(prev - h) < 0.5) return;
    this.setState({ pinnedTooltipHeight: h });
  };

  componentDidMount() {
    // First-run tutorial. Same pattern as ArenaStore. Hydrate the
    // on-disk seen flag, then if it's still unseen for this profile
    // open the overlay after a short delay so the layout settles.
    const profileId = this.props.profile?.id;
    if (profileId) {
      hydrateTutorialState().then(() => {
        if (this._unmounted) return;
        if (!shouldAutoPlay(profileId, SKILL_TREE_TUTORIAL_KEY)) return;
        this._tutorialTimer = setTimeout(() => {
          if (!this._unmounted) this.setState({ showSkillTreeTutorial: true });
        }, 500);
      });
    }
  }

  handleSkillTreeTutorialDismiss = () => {
    const profileId = this.props.profile?.id;
    if (profileId) markTutorialSeen(profileId, SKILL_TREE_TUTORIAL_KEY);
    this.setState({ showSkillTreeTutorial: false });
  };

  componentWillUnmount() {
    this._unmounted = true;
    if (this._confirmTimer) {
      clearTimeout(this._confirmTimer);
      this._confirmTimer = null;
    }
    if (this._tutorialTimer) {
      clearTimeout(this._tutorialTimer);
      this._tutorialTimer = null;
    }
  }

  // Total skill points available is derived from the player's level,
  // which we compute from `xp` via the same curve the rest of the
  // app uses. The profile object only carries `xp`. `level` isn't a
  // stored field, so we have to recompute it here.
  getPlayerLevel() {
    const profile = this.props.viewProfile || this.props.profile;
    return levelFromXp(profile?.xp || 0);
  }

  getAvailablePoints() {
    if (isDevBuild() && this.props.isAdmin) return TOTAL_SKILL_POINTS;
    // Reads from the server-claimed grantedSkillPoints on the profile
    // snapshot (or its nested `progression` block). See Progression
    // screen. Falls back to 0 for legacy profiles in flight.
    const profile = this.props.viewProfile || this.props.profile;
    return getAvailableSkillPoints(profile);
  }

  // Effective player level for node-status checks. Dev/admin always
  // gets the level cap so the level-10 / level-20 gates don't block
  // testing while we're iterating on the tree.
  getEffectiveLevel() {
    if (isDevBuild() && this.props.isAdmin) return 30;
    return this.getPlayerLevel();
  }

  // Paid respec. Confirmation first (this both spends gold and wipes
  // the tree), then the charge. The server owns the price and the
  // affordability check; the local guards below only keep the button
  // from firing a call that's certain to be rejected.
  handleResetSkills = async () => {
    if (this.props.viewProfile || this.state.resetting) return;
    const coins = this.props.profile?.coins || 0;
    if (countSpent(this.state.skillState) === 0) return;
    if (coins < SKILL_RESET_COST) {
      toast.error(t('You need {n} gold to reset your skill tree.', { n: SKILL_RESET_COST }));
      return;
    }
    const ok = await askConfirm({
      title: t('Reset your skill tree?'),
      message: t('This refunds every spent point and clears your chosen element for {n} gold. You keep all the points you have earned.', { n: SKILL_RESET_COST }),
      // Same price-then-verb shape as the button that opened this, so
      // the thing you click to confirm looks like the thing you
      // clicked. Currency mark stays gold on the danger button's red;
      // it's the app-wide mark for gold, not part of the label.
      confirmLabel: (
        <span className="inline-flex items-center gap-2.5">
          <span
            className="inline-flex items-center gap-1.5 tabular-nums"
            style={{ color: COIN_ICON_COLOR }}
          >
            <CoinIcon size={13} glow={false} />
            {SKILL_RESET_COST}
          </span>
          <span aria-hidden="true" className="self-stretch w-px" style={{ background: 'currentColor', opacity: 0.3 }} />
          <span>{t('Reset')}</span>
        </span>
      ),
      cancelLabel: t('Cancel'),
      danger: true,
    });
    if (!ok) return;

    this.setState({ resetting: true });
    try {
      const next = await purchaseSkillReset();
      playUI(UI.GOLD);
      this.setState({
        skillState: next,
        resetting: false,
        hoveredNode: null,
        pickerPath: null,
        pickerPendingNode: null,
        pendingPurchase: null,
      });
      toast.success(t('Skill tree reset. Every point is yours to spend again.'));
    } catch (err) {
      this.setState({ resetting: false });
      toast.error(err?.status === 402
        ? t('You need {n} gold to reset your skill tree.', { n: SKILL_RESET_COST })
        : t('Failed to reset your skill tree. Try again.'));
    }
  };

  handleNodeClick = (path, node, e) => {
    // Read-only view of another player's tree. No spends allowed.
    if (this.props.viewProfile) return;
    const { skillState } = this.state;
    const status = getNodeStatus(node.key, skillState, this.getEffectiveLevel());
    if (status === 'purchased') return;
    if (status === 'locked') return;

    const available = this.getAvailablePoints();
    if (countSpent(skillState) >= available) return;

    const anchorRect = e?.currentTarget?.getBoundingClientRect?.() || null;

    // Augmentation requires an element before any point can land in
    // the path. Open the picker first, remember the node + anchor
    // rect, then chain into the pinned confirmation once committed.
    const blocker = getInvestmentBlocker(path.key, skillState);
    if (blocker === 'pick-element') {
      playUI(UI.CLICK);
      this.setState({
        pickerPath: path.key,
        pickerPendingNode: { path, node, anchorRect },
      });
      return;
    }

    // All other paths pin the tooltip in place with confirm/cancel
    // buttons attached. spendPoint() never runs without an explicit
    // confirm click since skill choices are permanent.
    playUI(UI.CLICK);
    this.setState({
      pendingPurchase: { path, node, anchorRect, confirming: false },
      hoveredNode: null,
      pinnedTooltipHeight: null,
    });
  };

  handleConfirmPurchase = () => {
    if (this.props.viewProfile) return;
    const { pendingPurchase, skillState } = this.state;
    if (!pendingPurchase || pendingPurchase.confirming) return;
    const status = getNodeStatus(pendingPurchase.node.key, skillState, this.getEffectiveLevel());
    if (status !== 'available') {
      this.setState({ pendingPurchase: null, pinnedTooltipHeight: null });
      return;
    }
    const available = this.getAvailablePoints();
    if (countSpent(skillState) >= available) {
      this.setState({ pendingPurchase: null, pinnedTooltipHeight: null });
      return;
    }
    playUI(UI.CONFIRM);

    // Kick off the sweep animation, then spend the point and dismiss
    // the pinned tooltip once the visual finishes. Duration must
    // match the `vh-skill-tooltip-sweep` keyframe length in CSS.
    // The server call runs in parallel with the animation; if the
    // server rejects (e.g. budget mismatch on a tampered client) we
    // log the error, restore the previous state, and dismiss the
    // tooltip so the UI doesn't lie about a successful spend.
    this.setState({ pendingPurchase: { ...pendingPurchase, confirming: true } });
    if (this._confirmTimer) clearTimeout(this._confirmTimer);
    const node = pendingPurchase.node;
    const animationPromise = new Promise((r) => { this._confirmTimer = setTimeout(r, 700); });
    Promise.all([animationPromise, spendPoint(node.key)])
      .then(([, next]) => {
        this.setState({ skillState: next, pendingPurchase: null, pinnedTooltipHeight: null });
      })
      .catch((err) => {
        console.error('Failed to spend skill point:', err);
        this.setState({
          skillState: getSkillState(),
          pendingPurchase: null,
          pinnedTooltipHeight: null,
        });
      });
  };

  handleCancelPurchase = () => {
    if (this.state.pendingPurchase?.confirming) return;
    playUI(UI.CLICK);
    this.setState({ pendingPurchase: null, pinnedTooltipHeight: null });
  };

  handleHoverNode = (path, node, isHover, e) => {
    if (isHover) {
      // Capture the node's bounding rect at hover time so the
      // tooltip can position itself relative to the node and flip
      // sides if it would otherwise overflow the viewport.
      const anchorRect = e?.currentTarget?.getBoundingClientRect?.() || null;
      let displayNode = node;
      // Surface a "Requires Level X" line in the tooltip when the
      // player isn't there yet. Clarifies why the node is locked,
      // separate from the "previous node not yet purchased" case.
      const minLevel = getNodeMinLevel(node.key);
      if (minLevel && this.getEffectiveLevel() < minLevel) {
        displayNode = { ...displayNode, levelLockNeeded: minLevel };
      }
      this.setState({ hoveredNode: { path, node: displayNode, anchorRect } });
    } else if (this.state.hoveredNode?.node?.key === node.key) {
      this.setState({ hoveredNode: null });
    }
  };

  handlePickElement = async (elementKey) => {
    if (this.props.viewProfile) return;
    const pending = this.state.pickerPendingNode;
    try {
      const next = await setHunterElement(elementKey);
      this.setState({
        skillState: next,
        pickerPath: null,
        pickerPendingNode: null,
        pendingPurchase: pending ? { ...pending, confirming: false } : null,
        hoveredNode: null,
      });
      playUI(UI.CLICK);
    } catch (err) {
      console.error('Failed to set element:', err);
      this.setState({
        skillState: getSkillState(),
        pickerPath: null,
        pickerPendingNode: null,
      });
    }
  };

  handleClosePicker = () => {
    this.setState({ pickerPath: null, pickerPendingNode: null });
  };

  renderNode(path, node, idx) {
    const { skillState } = this.state;
    const playerLevel = this.getEffectiveLevel();
    const status = getNodeStatus(node.key, skillState, playerLevel);
    const minLevel = getNodeMinLevel(node.key);
    // The level badge appears next to the third skill (Lvl 10) and
    // the capstone (Lvl 20) ONLY while the player is below the
    // required level. Once they reach the level the badge
    // disappears entirely; the locked state is then conveyed by
    // the regular "previous skill not yet bought" styling.
    const levelMet = playerLevel >= minLevel;
    const showLevelBadge = minLevel > 0 && !levelMet;
    // Apply an extra grey/disabled treatment to nodes the player
    // can't reach because their level is too low. This is on top
    // of the regular locked styling so a level-gated node reads as
    // "wait for level X" rather than "you just need to buy the
    // previous one".
    const levelGated = minLevel > 0 && !levelMet;
    const cls = `vh-skill-node ${node.capstone ? 'vh-skill-node-capstone' : ''} vh-skill-node-${status}${levelGated ? ' vh-skill-node-level-gated' : ''}`;

    // Compositor-only halo. Replaces the keyframe-on-shadow
    // animations the button used to carry. Animated `box-shadow`
    // and `filter: drop-shadow` repaint every frame, while a
    // sibling `<div>` with a static shadow + animated `opacity`
    // composites for free. See the halo CSS block in index.css.
    let haloClass = null;
    if (status === 'available') {
      haloClass = node.capstone ? 'vh-skill-capstone-pulse-halo' : 'vh-skill-node-pulse-halo';
    } else if (status === 'purchased' && node.capstone) {
      haloClass = 'vh-skill-capstone-aura-halo';
    }

    return (
      <div key={node.key} className="vh-skill-node-wrap">
        <button
          type="button"
          className={cls}
          onClick={(e) => this.handleNodeClick(path, node, e)}
          onMouseEnter={(e) => this.handleHoverNode(path, node, true, e)}
          onMouseLeave={(e) => this.handleHoverNode(path, node, false, e)}
          style={node.capstone ? {
            '--vh-capstone-ornament-svg': `url("/ornaments/path-${path.key}.svg")`,
            '--vh-capstone-ornament-accent': path.accent,
          } : undefined}
        >
          {haloClass ? <span className={haloClass} aria-hidden="true" /> : null}
          <div className="vh-skill-node-inner">
            {node.capstone ? (
              <>
                <div className="vh-skill-capstone-ornament" />
                {/* Second ornament copy stacked on top of the base
                    in the purchased state, carrying the peak
                    drop-shadow with an opacity-pulsed animation.
                    compositor-friendly substitute for the original
                    `filter: drop-shadow` keyframe on the base. */}
                {status === 'purchased' ? (
                  <div
                    className="vh-skill-capstone-ornament vh-skill-capstone-ornament-glow-layer"
                    aria-hidden="true"
                  />
                ) : null}
              </>
            ) : (
              <div className="vh-skill-node-ornament" aria-hidden="true" />
            )}
          </div>
        </button>
        {showLevelBadge ? (
          <div
            className="vh-skill-node-level-tag"
            title={t('Requires Level {n}', { n: minLevel })}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ width: '0.5625cqw', height: '0.5625cqw' }}>
              <rect x="5" y="11" width="14" height="10" rx="1.5" />
              <path d="M8 11V8a4 4 0 0 1 8 0v3" />
            </svg>
            <span>{t('Lvl {n}', { n: minLevel })}</span>
          </div>
        ) : null}
      </div>
    );
  }

  renderLink(path, idx) {
    const { skillState } = this.state;
    const prevPurchased = skillState.pointsSpent.includes(path.nodes[idx - 1].key);
    return (
      <div className={`vh-skill-link ${prevPurchased ? 'vh-skill-link-active' : ''}`} />
    );
  }

  renderPath(path) {
    const { skillState } = this.state;
    const Icon = PATH_ICONS[path.key];
    const elementChoice = path.needsElement
      ? HUNTER_ELEMENTS.find((e) => e.key === skillState.hunterElement) || null
      : null;

    return (
      <div
        key={path.key}
        className="vh-skill-path"
        style={{
          '--vh-path-glow': `${path.accent}33`,
          '--vh-skill-node-accent': path.accent,
        }}
      >
        <img
          src="/ornaments/skilltree-backdrop.svg"
          alt=""
          aria-hidden="true"
          className="vh-skill-path-backdrop"
        />
        <div className="vh-skill-path-header">
          {Icon ? (
            <Icon
              className="vh-skill-path-icon"
              style={{ color: path.accent }}
              width={36}
              height={36}
            />
          ) : null}
          <div
            className="vh-skill-path-title arena-heading"
            style={{ color: path.accent }}
          >
            {path.title.replace('The ', '')}
          </div>
          <div className="vh-skill-path-tagline">{path.tagline}</div>
        </div>

        {/* Reserved picker slot. Always rendered so all five paths
            line up their nodes vertically. Augmentation shows the
            chosen element as a read-only nameplate once picked; the
            picker itself opens implicitly when the player clicks the
            first skill in the column without an element committed
            yet, so there's no separate "Choose Element" button. */}
        <div className="vh-skill-picker-slot">
          {path.needsElement && elementChoice ? (
            <div
              className="inline-flex items-center gap-2"
              title={t('Element choice is locked in. Only a skill tree reset clears it')}
              style={{
                padding: '6px 14px',
                borderRadius: 'var(--radius-md)',
                fontFamily: 'FantaisieArtistique, cursive',
                fontSize: '0.8125cqw',
                color: '#f0dca8',
                background: 'linear-gradient(180deg, rgba(32, 24, 14, 1) 0%, rgba(16, 12, 7, 1) 100%)',
                textShadow: '0 1px 2px rgba(0,0,0,0.7)',
                boxShadow:
                  'inset 0 1px 0 rgba(255,235,180,0.2), inset 0 -1px 0 rgba(0,0,0,0.3),' +
                  '0 0 0 1px rgba(232,195,110,0.55), 0 0 0 2px rgba(0,0,0,0.5),' +
                  '0 0 0 3px rgba(212,168,67,0.20)',
              }}
            >
              <img
                src={`/gameboard-tokens/${elementChoice.key}-token.svg`}
                alt=""
                width={16}
                height={16}
                style={{ flexShrink: 0 }}
              />
              <span>{t(elementChoice.label)}</span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ width: '0.625cqw', height: '0.625cqw', opacity: 0.7, marginLeft: 2 }}>
                <rect x="5" y="11" width="14" height="10" rx="1.5" />
                <path d="M8 11V8a4 4 0 0 1 8 0v3" />
              </svg>
            </div>
          ) : null}
        </div>

        {/* Vertical chain of nodes with linking lines between them. */}
        {path.nodes.map((node, idx) => (
          <div key={node.key} className="flex flex-col items-center">
            {idx > 0 ? this.renderLink(path, idx) : null}
            {this.renderNode(path, node, idx)}
          </div>
        ))}

      </div>
    );
  }

  renderPicker() {
    const { pickerPath, skillState } = this.state;
    if (!pickerPath) return null;

    // Only Hunter has a picker now. Choose one of four elements.
    const current = skillState.hunterElement;

    return (
      <motion.div
        key="picker"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[110] flex items-center justify-center"
        style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)' }}
        onClick={this.handleClosePicker}
      >
        <motion.div
          initial={{ y: 12, scale: 0.97 }}
          animate={{ y: 0, scale: 1 }}
          exit={{ y: 8, scale: 0.97 }}
          onClick={(e) => e.stopPropagation()}
          className="relative p-8 rounded-xl"
          style={{
            background: `url("/tex-noise-panel.webp"), linear-gradient(180deg, rgba(14,11,8,0.94), rgba(8,6,4,0.96))`,
 backgroundBlendMode: 'soft-light, normal',
 backdropFilter: 'blur(12px)',
 WebkitBackdropFilter: 'blur(12px)',
 boxShadow:
 'inset 0 1px 0 rgba(255,220,140,0.08), inset 0 -1px 0 rgba(0,0,0,0.3),' +
 '0 0 0 1px rgba(212,168,67,0.4), 0 0 0 3px rgba(0,0,0,0.5),' +
 '0 0 0 4px rgba(180,140,60,0.14), 0 20px 48px rgba(0,0,0,0.7)',
 width: '33.75cqw',
 maxWidth: '92vw',
 }}
 >
 <div className="flex items-center justify-center gap-4 mb-2" aria-hidden="true">
 <KnotOrnament side="left" size={22} />
 <h2
 className="arena-heading font-bold"
 style={{
 color: TEXT_PRIMARY,
 fontSize: '1.375cqw',
 textShadow: '0 2px 6px rgba(0,0,0,0.7), 0 0 14px rgba(212,168,67,0.3)',
 }}
 >
 {t('Choose an Element')}
 </h2>
 <KnotOrnament side="right" size={22} />
 </div>
 <p className="text-center text-[0.6875cqw] italic mb-2" style={{ color: TEXT_MUTED }}>
 {t('Cards of this element will roll foil more often as you progress through the Augmentation path.')}
 </p>
 <p
 className="text-center text-[0.6875cqw] mb-6 arena-heading"
 style={{
 color: '#e89090',
 textShadow: '0 1px 2px rgba(0,0,0,0.7)',
 }}
 >
 ⚠ {t('Choose carefully. Your element cannot be changed later.')}
 </p>
 <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
 {HUNTER_ELEMENTS.map((el) => (
 <button
 key={el.key}
 type="button"
 onClick={() => this.handlePickElement(el.key)}
 className={`vh-skill-picker-tile flex items-center gap-3 ${current === el.key ? 'vh-skill-picker-tile-selected' : ''}`}
                style={{ padding: '14px 18px', fontSize: '0.9375cqw' }}
              >
                <img
                  src={`/gameboard-tokens/${el.key}-token.svg`}
                  alt=""
                  width={28}
                  height={28}
                  style={{ flexShrink: 0 }}
                />
                <span>{t(el.label)}</span>
              </button>
            ))}
          </div>
          <div className="flex justify-center mt-5">
            <button
              type="button"
              onClick={this.handleClosePicker}
              className="text-[0.6875cqw] px-4 py-1.5"
              style={{ color: TEXT_MUTED, background: 'transparent', border: 'none', cursor: 'pointer' }}
            >
              {t('Close')}
            </button>
          </div>
        </motion.div>
      </motion.div>
    );
  }

  render() {
    const { onClose } = this.props;
    const { skillState, hoveredNode } = this.state;

    const available = this.getAvailablePoints();
    const spent = countSpent(skillState);
    const remaining = available - spent;
    const canAffordReset = (this.props.profile?.coins || 0) >= SKILL_RESET_COST;

    // Inline `mask-image` URL: Bun's CSS bundler strips `url('/...')`
    // from .css files, but inline styles are runtime values that
    // sail through untouched.
    const ORNAMENT_URL = 'url("/ornaments/viking-style2d-007.svg")';

    return (
      <div
        className="fixed inset-0 z-[100] flex flex-col"
        style={{
          // Plain layered backgrounds. The noise texture stacks on
          // top of the atmosphere gradient at its native alpha. We
          // used to apply `backgroundBlendMode: 'overlay, normal'`
          // here for a softer mix, but `mix-blend-mode` /
          // `background-blend-mode` forces an extra compositor pass
          // that's significantly more expensive on Windows than on
          // macOS Metal. And at this layer the noise is barely
          // visible under the rest of the UI anyway, so the visual
          // delta is negligible.
          background: `url("/tex-noise.webp"), ${BG_ATMOSPHERE}`,
          // Cascade the viking ornament URL down to every skill node so
          // the .vh-skill-node-ornament rule (which can't reference
          // url() directly. Bun's CSS minifier strips it) can pick
          // it up via var(--vh-skill-node-ornament-svg).
          '--vh-skill-node-ornament-svg': ORNAMENT_URL,
        }}
      >
        <div className="fixed inset-0 pointer-events-none" style={{ background: VIGNETTE }} />

        <div className="relative flex-1 flex flex-col min-h-0">

          {/* Ceremonial header. Same vocabulary as Settings / Play. */}
          <div className="relative shrink-0 flex items-center justify-center pt-10 pb-4 px-12">
            <div className="flex items-center gap-8" aria-hidden="true">
              <KnotOrnament side="left" size={32} />
              <div className="h-px w-24" style={{ background: `linear-gradient(90deg, transparent 0%, ${GOLD} 0.5) 100%)` }} />
              <h1
                className="arena-heading font-bold"
                style={{
                  color: TEXT_PRIMARY,
                  fontSize: '2.25cqw',
                  textShadow: '0 3px 8px rgba(0,0,0,0.8), 0 0 22px rgba(212,168,67,0.3)',
                }}
              >
                {t('Mastery')}
              </h1>
              <div className="h-px w-24" style={{ background: `linear-gradient(90deg, ${GOLD} 0.5) 0%, transparent 100%)` }} />
              <KnotOrnament side="right" size={32} />
            </div>
            <div className="absolute right-12 top-10">
              <CloseButton title={t('Close')} onClick={onClose} />
            </div>

            {/* Paid respec. Sits in the header-left slot, mirroring the
                close button, the same left-12 top-10 the Hall of Fame
                and Season screens use for their one left-hand header
                item. It's the only part of this screen with room: the
                path cards run nearly the full canvas height, so
                anything below the header collides with them.

                The wrapper carries the positioning because
                .vh-skill-action-btn sets `position: relative` itself,
                which beats Tailwind's `absolute` on the button.

                Hidden while viewing someone else's tree and while
                nothing is spent (a reset would be a pure gold sink
                with no effect). When the player can't afford it the
                button stays clickable but dimmed, so the click can
                explain why via toast. A `disabled` button swallows its
                own tooltip, the only other place that explanation
                could live. */}
            {!this.props.viewProfile && spent > 0 ? (
              <div className="absolute left-12 top-10">
                <button
                  type="button"
                  onClick={this.handleResetSkills}
                  disabled={this.state.resetting}
                  className="inline-flex items-center gap-3 vh-skill-action-btn vh-skill-action-btn-danger"
                  style={canAffordReset ? null : { opacity: 0.45, cursor: 'not-allowed' }}
                  title={canAffordReset
                    ? t('Refund every spent point for {n} gold', { n: SKILL_RESET_COST })
                    : t('You need {n} gold to reset', { n: SKILL_RESET_COST })}
                >
                  {this.state.resetting ? t('Resetting…') : (
                    <>
                      {/* Price first, in the same coin-then-amount
                          vocabulary the Store and Auction House use, so
                          the button reads as a purchase before it reads
                          as a reset. Gold on the price, the danger
                          variant's red on the label. */}
                      <span
                        className="inline-flex items-center gap-1.5 font-bold tabular-nums"
                        style={{ color: COIN_ICON_COLOR }}
                      >
                        <CoinIcon size={13} />
                        {SKILL_RESET_COST}
                      </span>
                      <span aria-hidden="true" className="self-stretch w-px" style={{ background: 'rgba(232,195,110,0.22)' }} />
                      <span>{t('Reset Skill Tree')}</span>
                    </>
                  )}
                </button>
              </div>
            ) : null}

            {/* Skill point HUD. Compact rounded plate pinned top-right
                next to the Close button. Frees the centre column for
                the trees while keeping the point budget glanceable
                at all times. */}
            <div className="absolute right-28 top-10 vh-skill-points-hud" data-tutorial="skill-points-hud">
              <span
                className="arena-heading font-bold tabular-nums vh-skill-points-hud-value"
                style={{
                  color: remaining > 0 ? '#f5e0a8' : TEXT_BODY,
                  textShadow: remaining > 0
                    ? '0 2px 4px rgba(0,0,0,0.8), 0 0 14px rgba(232,195,110,0.55)'
                    : '0 2px 4px rgba(0,0,0,0.8)',
                }}
              >
                {remaining}
              </span>
              <div className="vh-skill-points-hud-meta">
                <div className="vh-skill-points-hud-label">{t('Skill Points')}</div>
                <div className="vh-skill-points-hud-meta-sub">{t('{spent} / {total} earned', { spent, total: TOTAL_SKILL_POINTS })}</div>
              </div>
              <div className="vh-skill-points-hud-pips">
                {Array.from({ length: TOTAL_SKILL_POINTS }).map((_, i) => {
                  const earned = i < available;
                  const spentPip = i < spent;
                  return (
                    <div
                      key={i}
                      className={`vh-skill-pip ${spentPip ? 'vh-skill-pip-filled' : ''}`}
                      style={!earned ? { opacity: 0.22 } : null}
                    />
                  );
                })}
              </div>
            </div>
          </div>

          {/* The five paths. Flex row. Each path is a vertical chain
              of nodes with a header above and capstone at the bottom.
              No scroll: with cqw scaling, content is always sized to
              fit the canvas. */}
          <div className="flex-1 min-h-0 overflow-hidden">
            <div className="mx-auto max-w-[87.5cqw] w-full px-10 pt-6 pb-10">
              <div className="flex gap-3 items-start" data-tutorial="skill-paths-grid">
                {SKILL_PATHS.map((path) => this.renderPath(path))}
              </div>
            </div>
          </div>

        </div>

        {/* Floating tooltip. Fixed position, anchored next to the
            currently-hovered node. Lives at the screen root so it
            can escape any container clipping and overlay the path
            columns when needed. `pointer-events: none` ensures it
            never steals clicks meant for the node beneath it. */}
        {/* Click-outside backdrop for the pinned tooltip. Invisible
            (no dim/blur). The pin should feel inline, not modal. */}
        {this.state.pendingPurchase && !this.state.pendingPurchase.confirming ? (
          <div
            className="fixed inset-0 z-[105]"
            style={{ background: 'transparent' }}
            onClick={this.handleCancelPurchase}
          />
        ) : null}

        {/* Single tooltip layer. The pinned (mid-purchase) state takes
            priority over the hover state. When a node is clicked the
            same tooltip stays put with confirm/cancel buttons grafted
            on top, instead of being replaced by a separate modal. */}
        <AnimatePresence>
          {(() => {
            const pin = this.state.pendingPurchase;
            if (pin) {
              return (
                <SkillTooltip
                  key={`pinned-${pin.node.key}`}
                  path={pin.path}
                  node={pin.node}
                  anchorRect={pin.anchorRect}
                  pinned
                  confirming={pin.confirming}
                  onMeasure={this.handlePinnedTooltipMeasure}
                />
              );
            }
            if (hoveredNode) {
              return (
                <SkillTooltip
                  key={hoveredNode.node.key}
                  path={hoveredNode.path}
                  node={hoveredNode.node}
                  anchorRect={hoveredNode.anchorRect}
                />
              );
            }
            return null;
          })()}
        </AnimatePresence>

        {/* Confirm/cancel bar. Sibling of the tooltip, pinned just
            above it so the tooltip itself never resizes when the
            player clicks a node. */}
        <AnimatePresence>
          {this.state.pendingPurchase ? (
            <SkillTooltipConfirmBar
              path={this.state.pendingPurchase.path}
              anchorRect={this.state.pendingPurchase.anchorRect}
              confirming={this.state.pendingPurchase.confirming}
              onConfirm={this.handleConfirmPurchase}
              onCancel={this.handleCancelPurchase}
              tooltipHeight={this.state.pinnedTooltipHeight}
            />
          ) : null}
        </AnimatePresence>

        <AnimatePresence>{this.renderPicker()}</AnimatePresence>

        {this.state.showSkillTreeTutorial ? (
          <TutorialOverlay
            steps={skillTreeTutorialSteps()}
            onDismiss={this.handleSkillTreeTutorialDismiss}
          />
        ) : null}
      </div>
    );
  }
}
